import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { GradientTitle } from '../ui/typography/GradientTitle';
import { AccentButton } from '../ui/buttons/AccentButton';

// Define base URL for SEO
const SITE_URL = 'https://www.cognitivebiaslab.com';

interface BlogPost {
  _id: string;
  slug: string;
  title: string;
  summary: string;
  content: string;
  featured_image: string;
  author: string;
  published: boolean;
  published_at: string;
  created_at: string;
  updated_at: string;
  meta_description: string;
  keywords: string[];
  tags: string[];
  related_biases: string[];
}

// Local formatDate function to avoid import issues
const formatDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const Blog: React.FC = () => {
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showBanner, setShowBanner] = useState<boolean>(true);

  useEffect(() => {
    // Fetch blogs from MongoDB API
    setLoading(true);
    setError(null); // Clear any previous errors
    
    let errorTimer: NodeJS.Timeout | null = null;
    
    axios.get('/api/blogs')
      .then(response => {
        // Check if response.data is an array, otherwise use empty array
        const blogsData = Array.isArray(response.data) ? response.data : [];
        setBlogs(blogsData);
        setLoading(false);
        
        // Clear any pending error timer
        if (errorTimer) clearTimeout(errorTimer);
      })
      .catch(error => {
        console.error('Error fetching blogs:', error);
        
        // Set a timer before showing the error - allows content to load if it's just slow
        errorTimer = setTimeout(() => {
          setError(`Failed to load blog posts: ${error.message}`);
          setLoading(false);
        }, 2000); // 2 second delay before showing error
      });
    
    return () => {
      // Clean up timer if component unmounts
      if (errorTimer) clearTimeout(errorTimer);
    };
  }, []);

  // Ensure blogs is an array before filtering
  const publishedBlogs = Array.isArray(blogs) ? blogs.filter(blog => blog?.published) : [];

  return (
    <div className="blog-page bg-gradient-to-b from-orange-50 via-pink-50/50 to-orange-50/30 min-h-screen">
      <Helmet>
        <title>Blog - Cognitive Bias Lab</title>
        <meta name="description" content="Explore our latest articles about cognitive biases, decision making, and psychology." />
        <meta name="keywords" content="cognitive biases, psychology, decision making, critical thinking" />
        <meta property="og:title" content="Blog - Cognitive Bias Lab" />
        <meta property="og:description" content="Explore our latest articles about cognitive biases, decision making, and psychology." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${SITE_URL}/blog/`} data-source="page-component" />
      </Helmet>

      <div className="container mx-auto px-4 py-12">
        {/* Title Section with Standard GradientTitle */}
        <div className="text-center mb-8">
          <GradientTitle className="text-3xl md:text-4xl mb-4" useH1={true}>
            Our Latest Insights
          </GradientTitle>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Explore how cognitive biases influence your thinking, with practical strategies to make better decisions in everyday life.
          </p>
        </div>

        {/* Publishing Invitation Banner */}
        {showBanner && (
          <div className="relative mb-8 bg-white rounded-lg p-4 md:p-5 shadow-md max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex-1 mb-6 md:mb-0 pr-0 md:pr-4 text-center md:text-left pb-4 md:pb-0 border-b md:border-b-0 border-gray-100">
                <h3 className="font-bold text-lg text-gray-900 mb-1">Want to share your insights?</h3>
                <p className="text-gray-700 text-sm md:text-base">We welcome guest contributors passionate about cognitive biases and decision-making.</p>
              </div>
              <div className="flex-shrink-0 w-full md:w-auto md:pr-6 flex justify-center">
                <Link to="/contact/" className="w-full md:w-auto">
                  <AccentButton className="px-5 py-2 text-sm w-full md:w-auto">
                    Contact Us
                  </AccentButton>
                </Link>
              </div>
            </div>
            <button 
              onClick={() => setShowBanner(false)}
              className="absolute top-3 right-3 md:top-1/2 md:-translate-y-1/2 text-gray-400 hover:text-gray-700 p-1 bg-white rounded-full"
              aria-label="Close banner"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}

        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
          </div>
        )}

        {error && (
          <div className="bg-white rounded-xl shadow-md p-6 max-w-3xl mx-auto mb-6">
            <h2 className="text-xl font-bold mb-4">Explore Our Blog</h2>
            <p className="text-gray-700 mb-4">
              Our blog features articles about cognitive biases, decision-making, and practical psychology.
              We're constantly adding new content to help you understand how biases affect your thinking.
            </p>
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4" role="alert">
              <p>{error}</p>
            </div>
          </div>
        )}

        {!loading && publishedBlogs.length === 0 && !error && (
          <div className="bg-white rounded-xl shadow-md p-6 max-w-3xl mx-auto mb-6">
            <h2 className="text-xl font-bold mb-4">Explore Our Blog</h2>
            <p className="text-gray-700 mb-4">
              Our blog features articles about cognitive biases, decision-making, and practical psychology.
              We're currently preparing exciting new content for you.
            </p>
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mt-4" role="alert">
              <p>No blog posts found. Check back soon for new content!</p>
            </div>
          </div>
        )}

        {/* Blog Grid with Adjusted Width */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {publishedBlogs.map(blog => (
            <Link 
              to={`/blog/${blog.slug}/`} 
              key={blog._id} 
              className="group"
            >
              <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full flex flex-col max-w-sm mx-auto w-full">
                <div className="relative h-52 overflow-hidden bg-gradient-to-r from-orange-400 to-pink-500">
                  {blog.featured_image ? (
                    <img 
                      src={blog.featured_image} 
                      alt={blog.title} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full p-4">
                      <h2 className="text-white text-xl font-bold text-center">{blog.title}</h2>
                    </div>
                  )}
                </div>
                <div className="p-5 flex-grow flex flex-col">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {blog.tags && Array.isArray(blog.tags) && blog.tags.slice(0, 3).map((tag, index) => (
                      <span 
                        key={index} 
                        className="inline-block bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h3 className="font-bold text-xl mb-2 text-gray-800">{blog.title}</h3>
                  <p className="text-gray-700 mb-4 text-base line-clamp-3 flex-grow">{blog.summary}</p>
                  <div className="flex justify-between items-center text-sm text-gray-500 mt-auto">
                    <span>{blog.author}</span>
                    <span>{formatDate(new Date(blog.published_at))}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {blogs.length === 0 && !loading && !error && (
          <div className="text-center py-12">
            <p className="text-base text-gray-600">No blog posts available yet. Check back soon!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog; 